import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { ArrowLeft, FileCsv, Sparkle, Upload, Warning, X } from '@phosphor-icons/react';

import {
  bulkImport,
  matchColumns,
  translateRoleValues,
  type ImportRejected,
  type ImportResult,
  type ImportRow,
  type ImportValidationError,
  type MemberImportField,
} from '../api/members';
import { ApiError } from '../api/client';
import { LogoLoader } from './LogoLoader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  FIELDS,
  SHEET_KEY,
  applyLayout,
  applySheetTeamDefault,
  canImport,
  defaultLayouts,
  extractRawSheets,
  previewSheets,
  suggestionsToMapping,
  transformRows,
  uniqueRoleValuesFromRows,
  type FieldMapping,
  type ParsedFile,
} from '../lib/memberImport';

export interface ImportMembersModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
}

type Step = 'pick' | 'parsing' | 'map' | 'importing' | 'done';

// Multi-step flow:
//   pick      — user selects a CSV/XLSX file
//   parsing   — parse + fetch column suggestions (showed as a small spinner)
//   map       — field-driven mapping table; user adjusts dropdowns
//   importing — POST in flight
//   done      — summary (or, on validation error, drops back to map)

export function ImportMembersModal({ open, onClose, slug }: ImportMembersModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('pick');
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mapping, setMapping] = useState<FieldMapping>({});
  const [suggestionSource, setSuggestionSource] = useState<'ai' | 'fuzzy' | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ImportValidationError[] | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      reset();
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  function reset() {
    setStep('pick');
    setParsed(null);
    setMapping({});
    setSuggestionSource(null);
    setResult(null);
    setErrorCode(null);
    setValidationErrors(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // Async import: the server returns 202 Accepted immediately and runs the
  // actual import in a goroutine. The user gets a notification when it's
  // done; we close the modal as soon as the request is queued. Validation
  // errors still come back synchronously as a 400 since fixing those needs
  // an immediate feedback loop with the column mapping.
  const importMutation = useMutation({
    mutationFn: (rows: ImportRow[]) => bulkImport(auth.user?.access_token ?? null, slug, rows),
    onSuccess: () => {
      // Server queued the work and posted the "started" bell notification.
      // The modal is already closed (we close on confirm-click); just
      // refresh queries so the bell + members list pick up the activity
      // when the background goroutine finishes.
      void queryClient.invalidateQueries({ queryKey: ['notifications', slug] });
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
    },
    onError: (err) => {
      // The modal is already closed by this point — surface the failure
      // via a toast-equivalent so the user knows to retry. Validation
      // failures here are rare in practice (column-mapping happens
      // client-side first), so the alert is acceptable interim UX.
      let message: string;
      if (err instanceof ApiError && err.code === 'validation_failed') {
        const data = err.data as ImportRejected | null;
        const count = data?.validation_errors.length ?? 0;
        message = t('members.import.error.validation_failed', { count });
      } else {
        message = t('members.import.error.unknown');
      }
      console.error('bulk import failed', err);
      // Temporary: replace with the shared toast surface when one lands.
      window.alert(message);
    },
  });

  const onFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStep('parsing');
    setErrorCode(null);
    setValidationErrors(null);
    try {
      const rawSheets = await extractRawSheets(file);
      if (rawSheets.length === 0 || rawSheets.every((s) => s.rows.length === 0)) {
        setErrorCode('empty_file');
        setStep('pick');
        return;
      }
      // Send only the first PREVIEW_ROWS rows of each sheet to the AI; the
      // full matrix stays here and feeds applyLayout once the layouts come
      // back. One round-trip handles header-row detection, decorative-row
      // skipping, interleaved-separator filtering, AND column mapping.
      const result = await matchColumns(
        auth.user?.access_token ?? null,
        previewSheets(rawSheets),
      );
      const layouts = result.layouts.length > 0 ? result.layouts : defaultLayouts(rawSheets);
      const out = applyLayout(rawSheets, layouts);
      if (out.headers.length === 0 || out.rows.length === 0) {
        setErrorCode('empty_file');
        setStep('pick');
        return;
      }
      setParsed(out);
      setMapping(applySheetTeamDefault(suggestionsToMapping(result.suggestions), out.sheetNames));
      setSuggestionSource(result.source);
      setStep('map');
    } catch (err) {
      console.error('parse failed', err);
      setErrorCode('parse_error');
      setStep('pick');
    }
  };

  const ready = useMemo(() => parsed !== null && canImport(mapping), [parsed, mapping]);

  const onConfirmImport = () => {
    if (!parsed || !ready) return;
    setValidationErrors(null);

    // The import API is async (returns 202 + queues a background goroutine)
    // and posts both an "import_started" and an "import_completed" bell
    // notification. So the user doesn't need to sit on a loader screen —
    // close the modal immediately and let them carry on. Translation and
    // submission happen in the background; if the server hits a sync 400
    // (validation_failed) we re-open the modal via importMutation.onError.
    onClose();

    // Capture the parsed rows + mapping locally so a state reset / unmount
    // doesn't pull the rug out from under the in-flight request.
    const parsedNow = parsed;
    const mappingNow = mapping;
    const token = auth.user?.access_token ?? null;

    void (async () => {
      const uniqueValues = uniqueRoleValuesFromRows(parsedNow.rows, mappingNow);
      let translations = [] as Awaited<ReturnType<typeof translateRoleValues>>;
      if (uniqueValues.length > 0) {
        try {
          translations = await translateRoleValues(token, uniqueValues);
        } catch (err) {
          console.error('translateRoleValues failed; sending raw values', err);
        }
      }
      const rows = transformRows(parsedNow.rows, mappingNow, translations);
      importMutation.mutate(rows);
    })();
  };

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      style={{ width: 'min(48rem, calc(100vw - 2rem))' }}
    >
      <div className="modal__inner">
        <header className="modal__header">
          <h2>{t('members.import.title')}</h2>
          <button
            type="button"
            className="modal__close"
            aria-label={t('common.cancel')}
            onClick={onClose}
            disabled={importMutation.isPending}
          >
            <X size={20} aria-hidden />
          </button>
        </header>

        <div className="modal__body">
          {step === 'pick' && (
            <PickStep onPick={() => fileInputRef.current?.click()} errorCode={errorCode} />
          )}

          {step === 'parsing' && (
            <div
              role="status"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem',
                padding: '2rem 1rem',
              }}
            >
              <LogoLoader label={t('members.import.parsing')} size={220} />
              <p style={{ color: 'var(--color-fg-muted)', margin: 0 }}>
                {t('members.import.parsing')}
              </p>
            </div>
          )}

          {step === 'map' && parsed && (
            <MapStep
              parsed={parsed}
              mapping={mapping}
              source={suggestionSource}
              validationErrors={validationErrors}
              ready={ready}
              portalContainer={dialogRef.current}
              onChange={(field, column) =>
                setMapping((prev) => {
                  const next = { ...prev };
                  if (column === '') delete next[field];
                  else next[field] = column;
                  return next;
                })
              }
            />
          )}

          {step === 'importing' && (
            <div
              role="status"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem',
                padding: '2rem 1rem',
              }}
            >
              <LogoLoader label={t('members.import.importing')} size={220} />
              <p style={{ color: 'var(--color-fg-muted)', margin: 0 }}>
                {t('members.import.importing')}
              </p>
            </div>
          )}

          {step === 'done' && (
            <DoneStep result={result} errorCode={errorCode} />
          )}
        </div>

        <footer className="modal__footer">
          {step === 'map' && (
            <button type="button" className="btn btn--ghost" onClick={() => reset()}>
              <ArrowLeft size={16} aria-hidden />
              {t('common.back')}
            </button>
          )}
          {(step === 'pick' || step === 'parsing') && (
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
          )}
          {step === 'map' && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void onConfirmImport()}
              disabled={!ready || importMutation.isPending}
            >
              <Upload size={16} aria-hidden />
              {t('members.import.confirmCta', { count: parsed?.rows.length ?? 0 })}
            </button>
          )}
          {step === 'done' && (
            <button type="button" className="btn btn--primary" onClick={onClose}>
              {t('members.import.close')}
            </button>
          )}
        </footer>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          onChange={onFilePicked}
          style={{ display: 'none' }}
        />
      </div>
    </dialog>
  );
}

function PickStep({ onPick, errorCode }: { onPick: () => void; errorCode: string | null }) {
  const { t } = useTranslation('common');
  return (
    <div className="form" style={{ gap: '1rem' }}>
      <p>{t('members.import.body')}</p>

      <button
        type="button"
        onClick={onPick}
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
      >
        <FileCsv size={16} aria-hidden />
        {t('members.import.pickFile')}
      </button>

      <p className="form__hint">{t('members.import.formats')}</p>

      {errorCode && (
        <p role="alert" className="error">
          {t(`members.import.error.${errorCode}`, {
            defaultValue: t('members.import.error.unknown'),
          })}
        </p>
      )}
    </div>
  );
}

function MapStep({
  parsed,
  mapping,
  source,
  validationErrors,
  ready,
  onChange,
  portalContainer,
}: {
  parsed: ParsedFile;
  mapping: FieldMapping;
  source: 'ai' | 'fuzzy' | null;
  validationErrors: ImportValidationError[] | null;
  ready: boolean;
  onChange: (field: MemberImportField, column: string) => void;
  // Native <dialog>.showModal() puts the modal in the browser's top
  // layer; the parent passes its dialog ref so SelectContent portals
  // into the same layer instead of falling behind it.
  portalContainer?: HTMLElement | null;
}) {
  const { t } = useTranslation('common');

  // Sample preview per source column — single representative value. We
  // deliberately show ONE value (not two joined): a "Last name" preview that
  // says "Johanna Bengtsson · Frida Lundqvist" reads like one weird name and
  // misleads the user into thinking that string will land in their database.
  const samplesByColumn = useMemo(() => {
    const out: Record<string, string> = {};
    for (const h of parsed.headers) {
      let sample = '';
      for (const r of parsed.rows) {
        const v = (r[h] ?? '').trim();
        if (v) {
          sample = v;
          break;
        }
      }
      out[h] = sample || '—';
    }
    return out;
  }, [parsed]);

  const splitsCombinedName =
    !!mapping.first_name && mapping.first_name === mapping.last_name;

  // sheetsAsTeams is true when the user is opted in to the "use sheet names"
  // mapping. We surface a confirmation hint listing the sheet names so the
  // user can see what teams will be created.
  const sheetsAsTeams =
    mapping.team === SHEET_KEY && parsed.sheetNames.length > 1;
  // multiSheetFile gates whether the "Use sheet names" option appears in
  // the team dropdown at all — single-sheet files have nothing to fall back
  // on, so the option would be confusing.
  const multiSheetFile = parsed.sheetNames.length > 1;

  // When first_name and last_name share a column, the preview for each
  // should show what that field will actually receive after the split, not
  // the raw cell value (which is the same for both rows and confusingly
  // suggests the whole "Anna Andersson" string lands in last_name).
  const previewFor = (field: MemberImportField, picked: string): string => {
    if (!picked) return '—';
    if (picked === SHEET_KEY) {
      // Show the first sheet name as the preview so the user can see what
      // value will land — same shape as a real column preview.
      return parsed.sheetNames[0] ?? '—';
    }
    const raw = samplesByColumn[picked] ?? '—';
    if (raw === '—') return raw;
    if (splitsCombinedName && (field === 'first_name' || field === 'last_name')) {
      const idx = raw.lastIndexOf(' ');
      if (idx > 0) {
        return field === 'first_name' ? raw.slice(0, idx) : raw.slice(idx + 1);
      }
    }
    return raw;
  };

  const ignoredColumns = useMemo(() => {
    const used = new Set<string>();
    for (const v of Object.values(mapping)) {
      if (v) used.add(v);
    }
    return parsed.headers.filter((h) => !used.has(h));
  }, [parsed.headers, mapping]);

  const errorsByRow = useMemo(() => {
    if (!validationErrors) return new Map<number, ImportValidationError[]>();
    const out = new Map<number, ImportValidationError[]>();
    for (const e of validationErrors) {
      const list = out.get(e.row_index) ?? [];
      list.push(e);
      out.set(e.row_index, list);
    }
    return out;
  }, [validationErrors]);

  return (
    <div className="form" style={{ gap: '1rem' }}>
      <p>
        {t('members.import.mapBody', {
          rows: parsed.rows.length,
          columns: parsed.headers.length,
        })}
      </p>

      {source === 'ai' && (
        <p className="form__hint" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Sparkle size={16} aria-hidden weight="fill" style={{ flexShrink: 0 }} />
          <span>{t('members.import.aiAssisted')}</span>
        </p>
      )}

      {sheetsAsTeams && (
        <p
          className="form__hint"
          style={{
            color: 'var(--color-fg)',
            background: 'var(--color-accent-soft)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {t('members.import.sheetsAsTeams', {
            count: parsed.sheetNames.length,
            sheets: parsed.sheetNames.join(', '),
          })}
        </p>
      )}

      {!ready && (
        <p
          role="alert"
          className="error"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Warning size={16} aria-hidden style={{ flexShrink: 0 }} />
          <span>{t('members.import.error.required_fields_missing')}</span>
        </p>
      )}

      {splitsCombinedName && (
        <p
          className="form__hint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: 'var(--color-fg)',
          }}
        >
          <Sparkle size={14} aria-hidden weight="fill" style={{ flexShrink: 0 }} />
          <span>{t('members.import.splitsCombined', { column: mapping.first_name })}</span>
        </p>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.5rem', width: '12rem' }}>
                {t('members.import.column.field')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>
                {t('members.import.column.sourceColumn')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>
                {t('members.import.column.preview')}
              </th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f) => {
              const picked = mapping[f.value] ?? '';
              return (
                <tr key={f.value}>
                  <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                    {t(`members.import.field.${f.value}`)}
                    {f.required && (
                      <span aria-hidden style={{ color: 'var(--color-error)', marginLeft: '0.25rem' }}>
                        {t('members.import.requiredMarker')}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    <Select
                      value={picked === '' ? '__ignore__' : picked}
                      onValueChange={(v) =>
                        onChange(f.value, v === '__ignore__' ? '' : v)
                      }
                    >
                      <SelectTrigger
                        aria-label={t(`members.import.field.${f.value}`)}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent container={portalContainer ?? null}>
                        <SelectItem value="__ignore__">
                          {t('members.import.field.ignore')}
                        </SelectItem>
                        {/* Synthetic option for the team field on
                            multi-sheet XLSX: each sheet name becomes a
                            team. Putting it first makes the auto-default
                            visible at a glance. */}
                        {f.value === 'team' && multiSheetFile && (
                          <SelectItem value={SHEET_KEY}>
                            {t('members.import.field.useSheetNames', {
                              count: parsed.sheetNames.length,
                            })}
                          </SelectItem>
                        )}
                        {parsed.headers.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td style={{ padding: '0.5rem', color: 'var(--color-fg-muted)' }}>
                    {previewFor(f.value, picked)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {ignoredColumns.length > 0 && (
        <p className="form__hint">
          {t('members.import.ignoredColumns', {
            count: ignoredColumns.length,
            columns: ignoredColumns.join(', '),
          })}
        </p>
      )}

      {errorsByRow.size > 0 && (
        <div role="alert" className="error" style={{ marginTop: '0.5rem' }}>
          <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
            {t('members.import.error.validation_failed', {
              count: validationErrors?.length ?? 0,
            })}
          </p>
          <ul style={{ marginLeft: '1rem', listStyle: 'disc' }}>
            {Array.from(errorsByRow.entries()).slice(0, 8).map(([rowIdx, errs]) => (
              <li key={rowIdx}>
                {t('members.import.error.rowDetail', {
                  row: rowIdx + 1,
                  detail: errs.map((e) => formatValidationDetail(t, e)).join(', '),
                })}
              </li>
            ))}
            {errorsByRow.size > 8 && (
              <li>{t('members.import.error.more_rows', { count: errorsByRow.size - 8 })}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function DoneStep({
  result,
  errorCode,
}: {
  result: ImportResult | null;
  errorCode: string | null;
}) {
  const { t } = useTranslation('common');

  if (errorCode) {
    return (
      <p role="alert" className="error">
        {t(`members.import.error.${errorCode}`, {
          defaultValue: t('members.import.error.unknown'),
        })}
      </p>
    );
  }

  if (!result) return null;

  return (
    <div className="form" style={{ gap: '0.5rem' }}>
      <p style={{ fontSize: '1rem', fontWeight: 600 }}>
        {t('members.import.result.imported', { count: result.imported })}
      </p>
      {result.merged > 0 && (
        <p>{t('members.import.result.merged', { count: result.merged })}</p>
      )}
      {result.teams_created > 0 && (
        <p>{t('members.import.result.teamsCreated', { count: result.teams_created })}</p>
      )}
      {result.guardian_links > 0 && (
        <p>{t('members.import.result.guardianLinks', { count: result.guardian_links })}</p>
      )}
      {result.skipped.length > 0 && (
        <details>
          <summary>{t('members.import.result.skipped', { count: result.skipped.length })}</summary>
          {/* Scrollable so the full list is reviewable inline without
              pushing the modal footer off-screen. max-height ≈ 18rem keeps
              the dialog usable on a 13" laptop while still showing 10-12
              rows at a time. The notices live only in this modal — once
              dismissed they're gone, so we let the user see all of them
              while it's open. */}
          <ul
            style={{
              marginLeft: '1rem',
              marginTop: '0.5rem',
              listStyle: 'disc',
              maxHeight: '18rem',
              overflowY: 'auto',
              paddingRight: '0.5rem',
            }}
          >
            {result.skipped.map((s, i) => (
              <li key={i} style={{ fontSize: '0.875rem' }}>
                {formatSkipReason(t, s)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// formatValidationDetail renders a translated "<Field> <reason>" fragment for
// a single ImportValidationError. Falls back to the literal field+reason when
// no specific i18n key is registered, so a future server-side reason still
// shows up legibly instead of vanishing.
function formatValidationDetail(
  t: ReturnType<typeof useTranslation>['t'],
  e: ImportValidationError,
): string {
  const fieldLabel = t(`members.import.field.${e.field}`, { defaultValue: e.field });
  return t(`members.import.error.reason.${e.reason}`, {
    field: fieldLabel,
    defaultValue: `${fieldLabel} ${e.reason}`,
  });
}

// formatSkipReason renders a translated "Row N: …" line for a single
// SkippedRow. Falls back to the raw reason when no key matches.
function formatSkipReason(
  t: ReturnType<typeof useTranslation>['t'],
  s: { row_index: number; reason: string; value?: string },
): string {
  // merged_with_existing has two variants depending on whether the source row
  // had an email to show. Keeps the message tight for anonymous merges.
  const reasonKey =
    s.reason === 'merged_with_existing' && !s.value
      ? 'merged_with_existing_no_value'
      : s.reason;
  return t(`members.import.skipReason.${reasonKey}`, {
    row: s.row_index + 1,
    value: s.value ?? '',
    defaultValue: t('members.import.skipReason.unknown_member_role', {
      row: s.row_index + 1,
      value: s.reason,
    }),
  });
}
